import { useState, useEffect } from 'react';
import type { User, UserInput } from '../../api';

interface Role {
  id: string;
  name: string;
}

interface UserFormProps {
  user: User | null;
  roles: Role[];
  onSubmit: (data: UserInput) => Promise<void> | void;
  onCancel: () => void;
  /** Show an Active checkbox and include is_active in the payload (local node). */
  showStatus?: boolean;
  passwordMinLength?: number;
  /** Optional helper text under the role select (e.g. local's group-access note). */
  roleNote?: string;
}

/**
 * Create/edit a user. Works on the canonical User shape (name/email/role_id/is_active);
 * each app's adapter maps to its backend fields. Status (is_active) is opt-in.
 */
export default function UserForm({ user, roles, onSubmit, onCancel, showStatus = false, passwordMinLength = 8, roleNote }: UserFormProps) {
  const [form, setForm] = useState({ name: '', email: '', password: '', role_id: '', is_active: true });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role_id: user.role_id || '',
        is_active: user.is_active !== false,
      });
    }
  }, [user]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const data: UserInput = { name: form.name, email: form.email, role_id: form.role_id };
      if (form.password) data.password = form.password;
      if (showStatus) data.is_active = form.is_active;
      await onSubmit(data);
    } finally {
      setLoading(false);
    }
  }

  const inputClass =
    'mt-1.5 w-full rounded-lg border border-border bg-input px-3 py-2 text-[13px] text-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-ring';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-[13px] font-medium text-foreground">Name *</label>
        <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
      </div>

      <div>
        <label className="block text-[13px] font-medium text-foreground">Email *</label>
        <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
      </div>

      <div>
        <label className="block text-[13px] font-medium text-foreground">
          Password {user ? '(leave blank to keep)' : '*'}
        </label>
        <input
          type="password"
          required={!user}
          minLength={passwordMinLength}
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          placeholder={`Minimum ${passwordMinLength} characters`}
          className={inputClass}
        />
      </div>

      <div>
        <label className="block text-[13px] font-medium text-foreground">Role *</label>
        <select required value={form.role_id} onChange={(e) => setForm({ ...form, role_id: e.target.value })} className={inputClass}>
          <option value="">Select a role</option>
          {(roles || []).map((role) => (
            <option key={role.id} value={role.id}>{role.name?.replace(/_/g, ' ')}</option>
          ))}
        </select>
        {roleNote && <p className="mt-1 text-[11px] text-muted-foreground">{roleNote}</p>}
      </div>

      {showStatus && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="h-4 w-4 rounded border-border bg-input text-brand accent-brand"
          />
          <label htmlFor="is_active" className="text-[13px] text-foreground">Active</label>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="rounded-lg px-4 py-2 text-[13px] font-medium text-muted-foreground hover:bg-muted transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={loading} className="rounded-lg bg-brand px-4 py-2 text-[13px] font-semibold text-white hover:bg-brand-dark disabled:opacity-50 transition-colors">
          {loading ? 'Saving…' : user ? 'Update User' : 'Create User'}
        </button>
      </div>
    </form>
  );
}
